/**
 * useDebounce Hook
 * Debounce values and callbacks for performance optimization
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * Debounce a value
 * Returns the value after it has stopped changing for the specified delay
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Debounce a callback function
 * Returns a debounced version of the callback that will only be called
 * after it stops being invoked for the specified delay
 */
export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): {
  /** The debounced callback */
  debouncedCallback: (...args: Parameters<T>) => void;
  /** Cancel any pending debounced call */
  cancel: () => void;
  /** Immediately invoke the callback */
  flush: (...args: Parameters<T>) => void;
  /** Whether there's a pending call */
  isPending: boolean;
} {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgsRef = useRef<Parameters<T> | null>(null);
  const [isPending, setIsPending] = useState(false);

  // Update callback ref on each render
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingArgsRef.current = null;
    setIsPending(false);
  }, []);

  const flush = useCallback((...args: Parameters<T>) => {
    cancel();
    callbackRef.current(...args);
  }, [cancel]);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      pendingArgsRef.current = args;
      setIsPending(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (pendingArgsRef.current) {
          callbackRef.current(...pendingArgsRef.current);
          pendingArgsRef.current = null;
        }
        timeoutRef.current = null;
        setIsPending(false);
      }, delay);
    },
    [delay]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    debouncedCallback,
    cancel,
    flush,
    isPending,
  };
}

/**
 * Throttle a callback function
 * Returns a throttled version that can only be called once per interval
 */
export function useThrottledCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  interval: number
): {
  /** The throttled callback */
  throttledCallback: (...args: Parameters<T>) => void;
  /** Whether the callback is currently throttled */
  isThrottled: boolean;
} {
  const callbackRef = useRef(callback);
  const lastCallRef = useRef<number>(0);
  const [isThrottled, setIsThrottled] = useState(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const throttledCallback = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallRef.current;

      if (timeSinceLastCall >= interval) {
        lastCallRef.current = now;
        setIsThrottled(true);
        callbackRef.current(...args);

        setTimeout(() => {
          setIsThrottled(false);
        }, interval);
      }
    },
    [interval]
  );

  return {
    throttledCallback,
    isThrottled,
  };
}

/**
 * Debounced search input hook
 * Combines input state management with debouncing
 */
export function useDebouncedSearch(
  initialValue: string = '',
  delay: number = 300
): {
  /** Current input value */
  value: string;
  /** Debounced search value */
  debouncedValue: string;
  /** Update the input value */
  setValue: (value: string) => void;
  /** Clear the search */
  clear: () => void;
  /** Whether the debounced value is pending */
  isPending: boolean;
} {
  const [value, setValueState] = useState(initialValue);
  const debouncedValue = useDebounce(value, delay);

  const isPending = useMemo(() => value !== debouncedValue, [value, debouncedValue]);

  const setValue = useCallback((newValue: string) => {
    setValueState(newValue);
  }, []);

  const clear = useCallback(() => {
    setValueState('');
  }, []);

  return {
    value,
    debouncedValue,
    setValue,
    clear,
    isPending,
  };
}

export default useDebounce;
